import { useCallback, useMemo, useRef } from 'react'

import { useModuleContext } from '@/contexts/ModuleContext'
import { Store } from '@/services/storage'
import classNames from 'classnames'
import { estimateWidgetAnalytics } from 'lib/@getethos/analytics/analyticsEvents'

import { EstimateWidgetModuleData } from '@/components/EstimateWidget/EstimateWidget'
import { useEstimateQuotesContext } from '@/components/EstimateWidget/contexts/EstimateQuotesContext'
import { useEstimateWidgetContext } from '@/components/EstimateWidget/contexts/EstimateWidgetContext'
import { EstimateWidgetProgress } from '@/components/EstimateWidget/types/widget'

import finalExpenseInfoFormStyles from './FinalExpenseInfoForm.module.scss'
import { FinalExpenseForm } from './Forms/FinalExpenseForm'

export const FinalExpenseInfoWrapper = ({
  moduleData,
}: {
  moduleData: EstimateWidgetModuleData
}) => {
  const touchedFieldsRef = useRef<Array<string>>([])

  const {
    widgetProgressControls: { setWidgetProgress },
    userDataControls: { setUserData, userData },
  } = useEstimateWidgetContext()

  const { userSelectedValues } = useEstimateQuotesContext()

  const {
    moduleControls: { setHideModules },
    footerControls: { hideFooter },
  } = useModuleContext()

  const store = Store.getInstance('localStorage')

  const goToResults = useCallback(() => {
    setWidgetProgress(EstimateWidgetProgress.PRICES)
    estimateWidgetAnalytics.continueToPrices({
      properties: {
        triedToEditAnswers: !!userSelectedValues,
      },
    })
  }, [hideFooter, setHideModules, setWidgetProgress, userSelectedValues])

  const handleSubmit = useCallback(
    (formValues: Record<string, unknown>, trackSalesforce = false) => {
      if (formValues?.region === 'NY') {
        window.location.assign(`/life/youre-in-new-york/`)
        return
      }

      setUserData(formValues)
      goToResults()
    },
    [setUserData, goToResults]
  )

  const handleOnBlur = useCallback(
    (field: string) => {
      if (touchedFieldsRef.current.includes(field)) return
      estimateWidgetAnalytics.userInteractedWithField({
        properties: {
          triedToEditAnswers: !!userSelectedValues,
          field,
          key: 'final_expense_form_touched',
        },
      })
      touchedFieldsRef.current.push(field)
    },
    [userSelectedValues]
  )

  const renderForm = useMemo(() => {
    return (
      <FinalExpenseForm
        onSubmit={handleSubmit}
        handleOnBlur={handleOnBlur}
        useOldFonts={moduleData.useOldFonts}
        useOldColors={moduleData.useOldColors}
        defaultValues={{
          birthDate: userData?.birthDate,
          gender: userData?.gender,
        }}
      />
    )
  }, [handleOnBlur, handleSubmit, userData])

  const title = 'FREE INSTANT ESTIMATE'

  return (
    <div className={finalExpenseInfoFormStyles.container}>
      <p
        className={classNames(
          'body-l-bold m-0 mb-8 leading-3 text-[rgba(5,89,79,1)]',
          {
            [finalExpenseInfoFormStyles.newFonts]: !moduleData.useOldFonts,
          }
        )}
      >
        {title}
      </p>
      {renderForm}
    </div>
  )
}
